import React from "react";
import { useTranslation } from "react-i18next";
import { useColors } from "../constants/Colors";
import {
  FlatAttributes,
  FlatDisplayAttribute,
} from "../constants/FlatAttributes";
import {
  FaHome,
  FaDog,
  FaCar,
  FaSeedling,
  FaWheelchair,
  FaWifi,
  FaTv,
  FaCity,
  FaRecycle,
  FaDumbbell,
  FaUtensils,
} from "react-icons/fa";
import {
  MdElevator,
  MdBalcony,
  MdFireplace,
  MdLocalLaundryService,
  MdAir,
  MdBathtub,
  MdPool,
  MdStore,
  MdSubway,
} from "react-icons/md";

interface FlatAttributesDisplayProps {
  selectedAttributes: string[] | null;
}

const FlatAttributesDisplay: React.FC<FlatAttributesDisplayProps> = ({
  selectedAttributes,
}) => {
  const { containerNeutralLightMedium, textNeutralDefault } = useColors();
  const { t } = useTranslation();

  const getAttributeDetails = (
    id: string
  ): FlatDisplayAttribute | undefined => {
    return FlatAttributes.find((attribute) => attribute.id === id);
  };

  const renderIcon = (icon: string, library: string) => {
    const iconStyle = {
      marginRight: "5px",
      color: textNeutralDefault,
      fontSize: "15px",
    };

    switch (library) {
      case "EMOJIE":
        return <span style={iconStyle}>{icon}</span>;
      case "FontAwesome5":
        // Map FontAwesome5 icons
        const faIconMap: { [key: string]: React.ReactElement } = {
          home: <FaHome style={iconStyle} />,
          dog: <FaDog style={iconStyle} />,
          car: <FaCar style={iconStyle} />,
          seedling: <FaSeedling style={iconStyle} />,
          wheelchair: <FaWheelchair style={iconStyle} />,
          wifi: <FaWifi style={iconStyle} />,
          tv: <FaTv style={iconStyle} />,
          city: <FaCity style={iconStyle} />,
          recycle: <FaRecycle style={iconStyle} />,
          dumbbell: <FaDumbbell style={iconStyle} />,
        };
        return faIconMap[icon] || null;
      case "MaterialCommunityIcons":
        // Map MaterialCommunityIcons to available icons (Material Design or FontAwesome)
        const mdIconMap: { [key: string]: React.ReactElement } = {
          elevator: <MdElevator style={iconStyle} />,
          balcony: <MdBalcony style={iconStyle} />,
          dishwasher: <FaUtensils style={iconStyle} />, // Using FontAwesome utensils as alternative
          fireplace: <MdFireplace style={iconStyle} />,
          "washing-machine": <MdLocalLaundryService style={iconStyle} />,
          "air-conditioner": <MdAir style={iconStyle} />,
          bathtub: <MdBathtub style={iconStyle} />,
          pool: <MdPool style={iconStyle} />,
          store: <MdStore style={iconStyle} />,
          subway: <MdSubway style={iconStyle} />,
        };
        return mdIconMap[icon] || null;
      default:
        return null;
    }
  };

  if (!selectedAttributes || selectedAttributes.length === 0) {
    return null;
  }

  return (
    <div>
      <h2
        className="text-left text-sm sm:text-base font-semibold mb-3"
        style={{ color: textNeutralDefault }}
      >
        {t("fullscreencard.attributes_display.title")}
      </h2>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        {selectedAttributes.map((id, index) => {
          const attribute = getAttributeDetails(id.toString());
          if (!attribute) return null;
          return (
            <div
              key={index}
              style={{
                padding: "7px",
                border: `1px solid ${containerNeutralLightMedium}`,
                borderRadius: "6px",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                color: textNeutralDefault,
              }}
            >
              {renderIcon(attribute.icon, attribute.library)}
              <span style={{ fontSize: "14px" }}>
                {t(
                  `fullscreencard.attributes_display.attributes.${attribute.id}`
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FlatAttributesDisplay;
